/**
 * Form validation rules, shared by the footer form and the contact wizard.
 *
 * These rules are deliberately mirrored in `public/api/contact.php`. Client-side checks
 * are for the visitor's benefit only — anyone can POST directly to the endpoint, so the
 * server repeats every one of them.
 *
 * Every validator returns `null` when the value is acceptable, or a short message written
 * for the person filling the form.
 */

export const LIMITS = {
  name: { min: 2, max: 80 },
  email: { max: 254 },
  phone: { min: 7, max: 20 },
  company: { max: 100 },
  message: { max: 2000 },
};

/**
 * Pragmatic email check: something@something.tld with no spaces.
 * Deliberately not RFC 5322 — that regex rejects almost nothing extra and wrongly flags
 * valid addresses. The real test is whether the mail we send arrives.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Digits with the usual separators; at least 7 actual digits. */
const PHONE_RE = /^[+]?[\d\s().-]{7,20}$/;

/** Must contain at least one letter, so "..." or "1234" is not accepted as a name. */
const HAS_LETTER = /\p{L}/u;

export const validateName = (value) => {
  const v = String(value || "").trim();
  if (!v) return "Please enter your name.";
  if (v.length < LIMITS.name.min) return "That looks too short — please enter your full name.";
  if (v.length > LIMITS.name.max) return `Please keep your name under ${LIMITS.name.max} characters.`;
  if (!HAS_LETTER.test(v)) return "Please enter your name using letters.";
  return null;
};

export const validateEmail = (value) => {
  const v = String(value || "").trim();
  if (!v) return "Please enter your email address.";
  if (v.length > LIMITS.email.max) return "That email address is too long.";
  if (!EMAIL_RE.test(v)) return "That doesn't look like a valid email — please check it.";
  return null;
};

/** Optional field: empty is valid. */
export const validatePhone = (value) => {
  const v = String(value || "").trim();
  if (!v) return null;
  const digits = v.replace(/\D/g, "");
  if (digits.length < LIMITS.phone.min) return "Please enter a complete phone number.";
  if (v.length > LIMITS.phone.max) return "That phone number is too long.";
  if (!PHONE_RE.test(v)) return "Please use digits, spaces, and + ( ) - only.";
  return null;
};

export const validateCompany = (value) => {
  const v = String(value || "").trim();
  if (v.length > LIMITS.company.max) return `Please keep this under ${LIMITS.company.max} characters.`;
  return null;
};

export const validateMessage = (value, { required = false } = {}) => {
  const v = String(value || "").trim();
  if (required && !v) return "Please tell us a little about your project.";
  if (required && v.length < 10) return "A sentence or two helps us reply properly.";
  if (v.length > LIMITS.message.max) return `Please keep this under ${LIMITS.message.max} characters.`;
  return null;
};

/**
 * Run a set of validators and return `{ field: message }` for whatever failed.
 * `rules` maps a field name to a function of the whole form.
 */
export const runValidators = (values, rules) =>
  Object.entries(rules).reduce((errors, [field, check]) => {
    const message = check(values[field], values);
    if (message) errors[field] = message;
    return errors;
  }, {});

/** Trim every string value before submitting, so " a@b.com " never reaches the server. */
export const trimValues = (values) =>
  Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v]),
  );

/**
 * Honeypot: a field hidden from people but not from naive bots.
 * A non-empty value means an automated submission, which we drop silently — telling a
 * bot why it failed only helps it retry.
 */
export const HONEYPOT_FIELD = "website_url";

export const honeypotProps = {
  name: HONEYPOT_FIELD,
  tabIndex: -1,
  autoComplete: "off",
  "aria-hidden": true,
  style: {
    position: "absolute",
    left: "-9999px",
    width: "1px",
    height: "1px",
    opacity: 0,
    pointerEvents: "none",
  },
};

export const isBot = (values) => Boolean(String(values[HONEYPOT_FIELD] || "").trim());

/**
 * Time trap support.
 *
 * The server rejects submissions that arrive within a few seconds of the form being
 * rendered — a person needs time to read and type, a script does not. Record this when
 * the form mounts and send it with the payload.
 */
export const TIMER_FIELD = "form_started_at";
